import { defineStore } from 'pinia';
import {
    makeDataState,
    makeDataStateGetters,
    type StateHolder,
    withStateLoading,
} from '@/util/dataState';
import { fetchCatStacks } from '@/api/catStacks';
import { verify } from '@/util/verify';
import type { CatStackValue } from '@/views/dicoms/ctScan/types';
import loadImage from '@/views/dicoms/ctScan/loadImage';
import { cloneDeep } from 'lodash';

export type CatStackState = {
    axial: CatStackValue;
    coronal: CatStackValue;
    sagittal: CatStackValue;
};

export const initialState: DicomStoreState = {
    catStacks: {} as CatStackState,
    ...makeDataState(),
};

export type DicomStoreState = {
    catStacks: CatStackState;
} & StateHolder;

export const useCtScanStore = defineStore({
    id: 'dicom-store',
    state: (): DicomStoreState => ({ ...cloneDeep(initialState) }),
    getters: {
        ...makeDataStateGetters(),
        axial(state): CatStackValue {
            return state.catStacks.axial;
        },
        coronal(state): CatStackValue {
            return state.catStacks.coronal;
        },
        sagittal(state): CatStackValue {
            return state.catStacks.sagittal;
        },
    },
    actions: {
        async load(caseId: number, signal?: AbortSignal): Promise<void> {
            console.info('Loading cat-stacks...');

            await withStateLoading(this, async () => {
                const catStacks = verify(await fetchCatStacks(caseId, { signal }), 'cat-stack');

                const axialImage = await loadImage(catStacks.axial.image_url, { signal });
                const coronalImage = await loadImage(catStacks.coronal.image_url, { signal });
                const sagittalImage = await loadImage(catStacks.sagittal.image_url, { signal });

                this.catStacks.axial = {
                    ...catStacks.axial,
                    spriteImage: verify(axialImage, 'no axial image'),
                    currentIndex: Math.round(catStacks.axial.count / 2),
                };

                this.catStacks.coronal = {
                    ...catStacks.coronal,
                    spriteImage: verify(coronalImage, 'no coronal image'),
                    currentIndex: Math.round(catStacks.coronal.count / 2),
                };

                this.catStacks.sagittal = {
                    ...catStacks.sagittal,
                    spriteImage: verify(sagittalImage, 'no sagittal image'),
                    currentIndex: Math.round(catStacks.sagittal.count / 2),
                };
            });
        },
    },
});
